import 'antd/dist/antd.less';
import Cookies from 'js-cookie';
import { Auth, Cache } from 'aws-amplify';
import get from 'lodash/get';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { Component } from 'react';
import { withApollo } from 'react-apollo';
import { BrowserRouter as Router } from 'react-router-dom';
import {
	graphql,
	isMobileShell,
	logout,
	parse,
	postToShell,
} from 'src/_shared/services/utils.js';
import { setApolloClient } from 'src/clientService';
import { RouterContext } from './_shared/contexts';
import { dashboardActions, userActions } from './actions.js';
import AppRouter from './app-router.component';
import { configureApp } from './app.config';
import { store, persistor, injectClient } from './store';
import './style.scss';
import ThemeProvider from './theme-provider.jsx';
import {
	GetFeatureFlagsByUser,
	GetUser,
} from 'src/_shared/api/graphql/custom/users';

const getCachedJwt = () => {
	try {
		const jwt = Cookies.get('jwt');
		return jwt;
	} catch (error) {
		console.log(error);
	}
};

// Amplify keeps the Cognito session under CognitoIdentityServiceProvider.*
// keys; federated (SAML/Google) sessions live in the Amplify Cache.
const hasStoredSession = () => {
	try {
		if (Cache.getItem('federatedToken')?.id_token) return true;
		return Object.keys(localStorage).some(
			(key) =>
				key.startsWith('CognitoIdentityServiceProvider.') &&
				key.endsWith('.LastAuthUser')
		);
	} catch {
		return false;
	}
};

const getCurrentUserState = () => {
	return get(store.getState(), 'user.currentUser', {});
};

// Run configuration scripts
configureApp();

/** True when a JWT's exp claim is in the past (or unreadable). */
const jwtExpired = (jwt) => {
	try {
		const { exp } = JSON.parse(
			atob(jwt.split('.')[1].replaceAll('-', '+').replaceAll('_', '/'))
		);
		return !exp || exp * 1000 <= Date.now();
	} catch {
		return true;
	}
};

class App extends Component {
	constructor(props) {
		super(props);
		this.state = {
			auth: !!getCachedJwt(),
			currentUserState: getCurrentUserState(),
			hasFetchedFeatureFlags: false,
			// Hold the router until restoreSession() has run, otherwise the login
			// page mounts first and clears the stored user (see componentDidMount).
			restoring: isMobileShell() && !getCachedJwt() && hasStoredSession(),
		};
		// Id of the user we've already refreshed this session (see
		// componentDidUpdate). An instance field rather than resettable state so a
		// refresh that changes companyId can't re-trigger itself into a fetch loop.
		this.refreshedUserId = null;
		this.onAuthentication = this.onAuthentication.bind(this);
	}

	componentDidMount() {
		// Configure store with the Apollo client
		injectClient(this.props.client);
		setApolloClient(this.props.client);
		this.setupStoreSubscription();
		if (this.state.restoring) this.restoreSession();
		this.tellShellWhoSignedIn(this.state.currentUserState);
		// In the mobile shell the page can sit in the background for days.
		// Re-pull the user when the app comes back so a company moved to the
		// eAI portal meanwhile is handed off on the next open (ShellHandoff).
		// visibilitychange does not reliably reach the page in the Capacitor
		// shell's WKWebView, so the shell also dispatches `erinshell:resume`;
		// the 30s throttle below folds a double fire into one refresh.
		if (isMobileShell()) {
			document.addEventListener('visibilitychange', this.onVisibilityChange);
			document.addEventListener('erinshell:resume', this.onVisibilityChange);
		}
	}

	componentWillUnmount() {
		document.removeEventListener('visibilitychange', this.onVisibilityChange);
		document.removeEventListener('erinshell:resume', this.onVisibilityChange);
	}

	onVisibilityChange = (event) => {
		const userId = this.state.currentUserState?.id;
		if (!userId) return;
		if (
			event?.type === 'visibilitychange' &&
			document.visibilityState !== 'visible'
		)
			return;
		const now = Date.now();
		if (now - (this.lastForegroundRefresh || 0) < 30 * 1000) return;
		this.lastForegroundRefresh = now;
		this.refreshCurrentUser(userId);
	};

	// `auth` is keyed on the jwt cookie, which has no expiry. WebKit drops
	// session cookies when the host app is killed (the ERIN mobile shell,
	// ERIN-8561), while the Amplify / federated tokens in storage survive, so
	// rebuild the cookie from them instead of sending the user back to login.
	// Shell only: in a browser a closed session still means "log in again".
	restoreSession = async () => {
		try {
			// A federated id token that has expired is not a session; fall
			// through to Amplify, which refreshes a password session itself.
			const federatedToken = Cache.getItem('federatedToken');
			const federated = federatedToken?.id_token;
			const token =
				(federated && !jwtExpired(federated) ? federated : null) ||
				(await Auth.currentSession()).getIdToken().getJwtToken();
			if (!token) return;
			Cookies.set('jwt', token, {
				secure: process.env.REACT_APP_INSECURE_COOKIES !== 'true',
			});
			this.setState({ auth: true });
		} catch {
			// No usable session: the login page is the right place.
		} finally {
			this.setState({ restoring: false });
		}
	};

	componentDidUpdate(prevProps, prevState) {
		const currentUser = this.state.currentUserState;
		const prevUser = prevState.currentUserState;

		// Refetch feature flags when the user or their company changes.
		if (
			prevUser?.id !== currentUser?.id ||
			prevUser?.companyId !== currentUser?.companyId
		) {
			this.setState({
				hasFetchedFeatureFlags: false,
			});
		}

		if (currentUser?.id && !this.state.hasFetchedFeatureFlags) {
			this.fetchFeatureFlags(currentUser.id);
		}

		// Refresh the persisted user once per identity. Keyed on id (not on
		// companyId) so the refresh's own setCurrentUser — which can change
		// companyId from the stale value to the fresh one — cannot re-trigger this.
		if (currentUser?.id && this.refreshedUserId !== currentUser.id) {
			this.refreshedUserId = currentUser.id;
			this.refreshCurrentUser(currentUser.id);
		}

		this.tellShellWhoSignedIn(currentUser);
	}

	// The mobile shell tags its push subscription with the signed-in user so
	// notifications can target them (ERIN-8561). Once per identity; the
	// logout page sends erin:signout.
	tellShellWhoSignedIn = (currentUser) => {
		if (!currentUser?.id) {
			// Signed out: the next sign-in (even as the same user) must re-tag.
			this.shellUserId = null;
			return;
		}

		if (this.shellUserId === currentUser.id || !isMobileShell()) return;
		this.shellUserId = currentUser.id;
		postToShell({
			type: 'erin:user',
			userId: currentUser.id,
			companyId: currentUser.companyId || currentUser.company?.id,
			departmentId: currentUser.departmentId || undefined,
		});
	};

	fetchFeatureFlags = async (userId) => {
		this.setState({ hasFetchedFeatureFlags: true });
		try {
			const { data } = await this.props.client.query({
				query: GetFeatureFlagsByUser,
				variables: { userId },
				fetchPolicy: 'network-only',
			});
			store.dispatch(
				userActions.updateFeatureFlags(data?.getFeatureFlagsByUser || [])
			);
			this.setState({ currentUserState: getCurrentUserState() });
		} catch (error) {
			console.log('Error fetching feature flags:', error);
		}
	};

	// Persisted `currentUser` (incl. company) is only overwritten on an explicit
	// login. Users with long-lived sessions (e.g. SSO that skips the login page)
	// can ride on a stale company object for days, so company settings such as
	// `disableManagerPermissionsByType` lag behind the server. Re-pull the user on
	// boot — same query/shape the support "login as" flow uses — to stay in sync.
	refreshCurrentUser = async (userId) => {
		try {
			const { data } = await this.props.client.query({
				query: GetUser,
				variables: { id: userId },
				fetchPolicy: 'network-only',
			});
			if (data?.getUser) {
				// SET_CURRENT_USER preserves the locally-held displayAs and the
				// freshly-fetched featureFlags, so this only refreshes server fields.
				store.dispatch(userActions.createSetCurrentUserAction(data.getUser));
				this.setState({ currentUserState: getCurrentUserState() });
			}
		} catch (error) {
			console.log('Error refreshing current user:', error);
		}
	};

	setupStoreSubscription = () => {
		if (!store) return;

		let currentDisplayAs;
		let isFetchingTranslations = false;

		store.subscribe(async () => {
			const state = store.getState();
			let languageCode = get(state, 'user.currentUser.languageCode');
			const currentAllMultiLingualDataCode = get(
				state,
				'dashboard.allMultiLingualData[0].languageCode'
			);
			languageCode ||= 'US';
			const newUserState = get(state, 'user.currentUser', {});
			if (newUserState !== this.state.currentUserState) {
				this.setState({ currentUserState: newUserState });
			}

			if (
				languageCode !== currentAllMultiLingualDataCode &&
				!isFetchingTranslations
			) {
				isFetchingTranslations = true;

				try {
					if (languageCode === 'US') {
						store.dispatch(
							dashboardActions.createSetMultiLingualData([
								{ languageCode: 'US' },
							])
						);
					} else {
						const getTranslationsByLanguageCode = async (token = null) => {
							const response = await graphql({
								input: {
									languageCode,
									nextToken: token,
								},
								query: 'queryMultiLingualByLanguageCodeIndex',
							});
							const items = get(response, 'items', []);
							const nextToken = get(response, 'nextToken');
							if (nextToken) {
								const moreTranslations =
									await getTranslationsByLanguageCode(nextToken);
								return [...items, ...moreTranslations];
							}

							return items;
						};

						const allTranslationsByLanguageCode =
							await getTranslationsByLanguageCode();
						store.dispatch(
							dashboardActions.createSetMultiLingualData(
								allTranslationsByLanguageCode
							)
						);
					}
				} catch (error) {
					console.error('Failed to fetch translations:', error);
				} finally {
					isFetchingTranslations = false;
				}
			}

			// Set displayAs based on user's role during initial login
			// Skip this if it was a failed login attempt
			if (get(state, 'user.currentUser')) {
				const {
					user: { currentUser },
				} = store.getState();
				const { role } = currentUser;

				if (currentDisplayAs !== role) {
					currentDisplayAs = role;
					store.dispatch(userActions.updateDisplayAs(role));
				}
			}
		});
	};

	onAuthentication = (authToken, currentUser) => {
		try {
			// if (!authToken) return;
			Cookies.set('jwt', authToken, {
				secure: process.env.REACT_APP_INSECURE_COOKIES !== 'true',
			});
			this.setState({
				auth: true, // authToken ? true : false,
			});
		} catch (error) {
			console.log(error);
		}
	};

	render() {
		if (!store || !persistor || this.state.restoring) {
			return <div>Loading...</div>;
		}

		const jwt = null;
		let providerValue = null;

		try {
			const currentUser = this.state.currentUserState;
			const supportAdminPermissions = currentUser?.admin
				? parse(currentUser?.admin)?.permissions === '*'
				: false;
			const platformConfig =
				typeof currentUser?.company?.platformConfig === 'string'
					? parse(currentUser?.company?.platformConfig)
					: {
							primaryPlatform: 'referrals',
							filterMode: 'exclusionary',
							filterList: [],
						};
			providerValue = {
				auth: !!getCachedJwt(),
				onAuthentication: this.onAuthentication,
				currentUser,
				client: this.props.client,
				handleSignOut: this.props.handleSignOut,
				primaryPlatform: platformConfig.primaryPlatform,
			};
			const userSignupSettings = currentUser?.company?.userSignupSettings
				? parse(currentUser?.company?.userSignupSettings)
				: {};
			const accountClaim = currentUser?.accountClaim;
			const userHasInactiveAccountClaim =
				userSignupSettings?.auth?.requireAccountClaimToAuthorize &&
				!accountClaim?.active;
			const { pathname } = window.location;
			if (
				currentUser?.id &&
				userHasInactiveAccountClaim &&
				!supportAdminPermissions &&
				pathname !== '/login' &&
				pathname !== '/logout' &&
				pathname !== '/'
			) {
				logout();
			}
		} catch (error) {
			console.log(error);
		}

		return (
			<Provider store={store}>
				<PersistGate loading={null} persistor={persistor}>
					<ThemeProvider>
						<RouterContext.Provider value={providerValue}>
							<Router>
								<AppRouter
									auth={this.state.auth}
									secrets={get(this.props, 'secrets')}
								/>
							</Router>
						</RouterContext.Provider>
					</ThemeProvider>
				</PersistGate>
			</Provider>
		);
	}
}

export default withApollo(App);
